import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, output, input } from '@angular/core';

import type moment from 'moment-timezone';

import { DateRangeValueType } from '@core/date-range-types';
import type { DateRangeValue, TDateRangeValueType } from '@core/date-range-types';
import { DateRangePickerComponent } from '@core/time-ui';
import type { DatePickerDirection } from '@core/time-ui';
import { OverlayPosition } from '@core/user-interface-legacy';

type ReportDateFilter =
  | {
      dateRange: DateRangeValueType;
      dateStart: null;
      dateEnd: null;
    }
  | {
      dateRange: 'range';
      dateStart: string;
      dateEnd: string;
    }
  | {
      dateRange: null;
      dateStart: null;
      dateEnd: null;
    };

export type ReportDateRange = 'range' | TDateRangeValueType;

export const defaultPossibleDateRanges = [
  DateRangeValueType.Today,
  DateRangeValueType.Yesterday,
  DateRangeValueType.ThisWeek,
  DateRangeValueType.LastWeek,
  DateRangeValueType.ThisMonth,
  DateRangeValueType.LastMonth,
  DateRangeValueType.ThisQuarter,
  DateRangeValueType.LastQuarter,
  DateRangeValueType.ThisYear,
  DateRangeValueType.LastYear,
  DateRangeValueType.Last30Days,
  DateRangeValueType.Last60Days,
  DateRangeValueType.Custom,
  DateRangeValueType.AllTime,
];

@Component({
  selector: 'reports-date-picker',
  templateUrl: 'reports-date-picker.component.html',
  styleUrls: ['reports-date-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DateRangePickerComponent],
})
export class ReportsDatePickerComponent {
  public readonly value = input<ReportDateRange>(undefined!);
  public readonly customRange = input<string[] | null>(null);
  public readonly label = input<string>(undefined!);
  public readonly placeholder = input<string>(undefined!);
  public readonly disabled = input<boolean>(false);
  public readonly dropdownPosition = input<OverlayPosition>(OverlayPosition.BottomRight);
  public readonly possibleDateRanges = input<DateRangeValueType[]>(defaultPossibleDateRanges);
  public readonly addClearOption = input(false);
  public readonly variant = input<'button' | 'input' | 'input-rounded'>('button');
  public readonly datePickerDirection = input<DatePickerDirection>(undefined!);

  public readonly blackout = input<((date: moment.Moment) => boolean) | null>(null);

  public dateRangeValue: DateRangeValue;

  public dateChange = output<ReportDateFilter>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.dateRangeValue = {
        ...this.dateRangeValue,
        type: changes.value.currentValue === 'range' ? 'custom' : changes.value.currentValue,
      };
    }

    if (changes.customRange) {
      const customRange = this.customRange();
      const customRangeValue = this.customRange();
      this.dateRangeValue = {
        ...this.dateRangeValue,
        min: customRange ? customRange[0] : null,
        max: customRangeValue ? customRangeValue[1] : null,
      };
    }
  }

  public handleDateRangeChange(value: DateRangeValue): void {
    if (value.type === 'custom') {
      this.dateChange.emit({
        dateRange: 'range',
        dateStart: value.min!,
        dateEnd: value.max!,
      });
    } else if (value.type === null) {
      this.dateChange.emit({
        dateRange: null,
        dateStart: null,
        dateEnd: null,
      });
    } else {
      this.dateChange.emit({
        dateRange: value.type,
        dateStart: null,
        dateEnd: null,
      });
    }
  }
}
